import { Image } from 'expo-image';
import * as React from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Text } from '@/ui';

import { Icon } from '../icon';

export enum ChipType {
  DEFAULT = 'default',
  SUCCESS = 'success',
  ERROR = 'error',
  PENDING = 'pending',
  WARNING = 'warning',
  BRAND = 'brand',
}

export interface ChipProps {
  type?: ChipType;
  icon?: React.ReactNode;
  imgSrc?: string;
  text: string;
  iconWidth?: number;
  iconHeight?: number;
  useDefaultIconFillColor?: boolean;
  iconFillColor?: string;
  style?: any;
  textStyle?: any;
}

export function Chip({
  type = ChipType.DEFAULT,
  icon,
  imgSrc,
  text,
  iconWidth = 16,
  iconHeight = 16,
  useDefaultIconFillColor = true,
  iconFillColor,
  style,
  textStyle,
}: ChipProps) {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={[styles.container, styles[`${type}Container`], style]}>
      {icon ? (
        <Icon
          type="naked"
          icon={icon}
          width={iconWidth}
          height={iconHeight}
          fillColor={
            useDefaultIconFillColor && styles[`${type}Icon`]
              ? styles[`${type}Icon`].color
              : iconFillColor
          }
        />
      ) : null}

      {imgSrc ? (
        <Image
          style={{ width: iconWidth, height: iconHeight }}
          source={imgSrc}
          contentFit="cover"
        />
      ) : null}

      <Text type="caption2" style={[styles[`${type}Text`], textStyle]}>
        {text}
      </Text>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexDirection: 'row',
    gap: 8,
    backgroundColor: theme.colors.chip.backgroundColor,
    borderRadius: 8,
    padding: 8,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
  },
  defaultContainer: {
    backgroundColor: theme.colors.chip.backgroundColor,
  },
  pendingContainer: {
    backgroundColor: theme.colors.chip.pending.backgroundColor,
    borderColor: theme.colors.chip.pending.border,
    borderWidth: 1,
  },
  successContainer: {
    backgroundColor: theme.colors.chip.success.backgroundColor,
    borderColor: theme.colors.chip.success.border,
    borderWidth: 1,
  },
  errorContainer: {
    backgroundColor: theme.colors.chip.error.backgroundColor,
    borderColor: theme.colors.chip.error.border,
    borderWidth: 1,
  },
  warningContainer: {
    backgroundColor: theme.colors.chip.warning.backgroundColor,
    borderColor: theme.colors.chip.warning.border,
    borderWidth: 1,
  },
  brandContainer: {
    backgroundColor: theme.colors.chip.brand.backgroundColor,
    borderColor: theme.colors.chip.brand.border,
    borderWidth: 1,
  },
  defaultIcon: {
    color: theme.colors.icon.color,
  },
  pendingIcon: {
    color: theme.colors.chip.pending.color,
  },
  successIcon: {
    color: theme.colors.chip.success.color,
  },
  errorIcon: {
    color: theme.colors.chip.error.color,
  },
  warningIcon: {
    color: theme.colors.chip.warning.color,
  },
  brandIcon: {
    color: theme.colors.chip.brand.color,
  },
  pendingText: {
    color: theme.colors.chip.pending.color,
  },
  successText: {
    color: theme.colors.chip.success.color,
  },
  errorText: {
    color: theme.colors.chip.error.color,
  },
  warningText: {
    color: theme.colors.chip.warning.color,
  },
  brandText: {
    color: theme.colors.chip.brand.color,
  },
}));
